
import React, { useEffect, useState } from "react";
import EnhancedResults from "@/components/quiz/EnhancedResults";
import type { Question } from "@/types/quiz";
import { processQuestionsWithDifficulty } from "@/utils/questionCategorization";
import { normalizeDifficulty } from "@/types/quiz";
import { useQuizSessionHistory } from "@/hooks/useQuizSessionHistory";
import { useQuizScore } from "@/hooks/useQuizScore";
import { useQuizState } from "@/hooks/useQuizState";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { toast } from "sonner";

interface ResultsProps {
  questions: Question[];
  answers: (number | null)[];
  onReset: () => void;
  sessionId?: string;
  subject?: string | null;
  category?: string | null;
  difficulty?: string;
}

const Results: React.FC<ResultsProps> = ({ 
  questions, 
  answers, 
  onReset, 
  sessionId,
  subject,
  category,
  difficulty
}) => {
  console.log("Results component rendering with:", {
    questionCount: questions?.length,
    answersCount: answers?.length,
    subject: subject,
    category: category,
    firstQuestionSubject: questions?.[0]?.subject,
    firstQuestionCategory: questions?.[0]?.category,
    difficulty: difficulty,
    sessionId: sessionId,
    questionIds: questions?.map(q => q.id)
  });

  // Store all question snapshots across different difficulty levels
  const [allQuestions, setAllQuestions] = useState<Question[]>([]);
  const [allAnswers, setAllAnswers] = useState<(number | null)[]>([]);
  const [performanceSaved, setPerformanceSaved] = useState(false);
  
  // Get quiz state for saving performance
  const { state, setState } = useQuizState();
  
  // Create a modified state with fallback values for subject/category if missing
  const stateWithFallbacks = {
    ...state,
    selectedSubject: subject || state.selectedSubject || questions?.[0]?.subject || "General",
    selectedCategory: category || state.selectedCategory || questions?.[0]?.category || "General",
    subject: subject || state.subject || questions?.[0]?.subject || "General",
    category: category || state.category || questions?.[0]?.category || "General"
  };
  
  // Get saveScore function from useQuizScore
  const { saveScore, isSaving } = useQuizScore(stateWithFallbacks, setState);
  
  const { 
    getSortedSnapshots, 
    resetSnapshots, 
    isResultSubmitted, 
    resetSubmissionStatus,
    getSessionStartTime
  } = useQuizSessionHistory();

  // Reset submission status when component unmounts
  useEffect(() => {
    return () => {
      resetSubmissionStatus();
    };
  }, [resetSubmissionStatus]);

  useEffect(() => {
    // Get the question snapshots from our dedicated hook - use all questions, don't limit to 10
    const snapshots = getSortedSnapshots();
    console.log("Found sorted and filtered snapshots:", snapshots.length);
    
    if (snapshots.length > 0) {
      console.log("Snapshot IDs:", snapshots.map((q) => q.id));
      console.log("Snapshot indexes:", snapshots.map((q) => q.shownAtIndex));
      console.log("Snapshot answers:", snapshots.map((q) => q.userAnswer));
      
      // Only include questions that were actually answered
      const answeredSnapshots = snapshots.filter(q => 
        q.userAnswer !== undefined && q.userAnswer !== null
      );
      
      if (answeredSnapshots.length > 0) {
        // Extract answers from snapshots - only include valid answers
        const extractedAnswers = answeredSnapshots.map((q) => q.userAnswer);
        
        setAllQuestions(answeredSnapshots);
        setAllAnswers(extractedAnswers);
        console.log("Using answered snapshots:", answeredSnapshots.length);
      } else if (questions.length > 0) {
        // Fallback to using provided questions and answers
        console.log("No answered snapshots found, using provided questions and answers");
        useFallbackQuestionsAndAnswers();
      }
    } else if (questions.length > 0) {
      console.log("No snapshots found, using provided questions and answers");
      useFallbackQuestionsAndAnswers();
    }
  }, [questions, answers, getSortedSnapshots]);

  const useFallbackQuestionsAndAnswers = () => {
    // Create a map of valid questions from the questions array (no duplicates)
    const uniqueQuestionsMap = new Map<string | number, Question>();
    const questionAnswerMap = new Map<string | number, number | null>();
    
    // First map questions to their answers
    questions.forEach((q, index) => {
      if (q.id && index < answers.length) {
        questionAnswerMap.set(q.id, answers[index]);
      }
    });
    
    // Then filter to only include questions that have answers
    questions.forEach((q, index) => {
      if (q.id && !uniqueQuestionsMap.has(q.id)) {
        const answer = questionAnswerMap.get(q.id);
        if (answer !== undefined && answer !== null) {
          uniqueQuestionsMap.set(q.id, {...q, shownAtIndex: index});
        }
      }
    });
    
    const uniqueQuestions = Array.from(uniqueQuestionsMap.values());
    console.log("Using unique answered questions from provided array:", uniqueQuestions.length);
    
    // Match answers to unique questions
    const validAnswers = uniqueQuestions.map((q) => {
      return questionAnswerMap.get(q.id) ?? null;
    });
    
    // Don't limit to 10 questions - use all available questions
    setAllQuestions(uniqueQuestions);
    setAllAnswers(validAnswers);
  };

  // Handle reset to also reset snapshots
  const handleReset = () => {
    resetSubmissionStatus();
    resetSnapshots();
    onReset();
  };
  
  // Handle saving performance data
  const handleSavePerformance = async () => {
    if (performanceSaved) {
      toast.info("Performance data already saved");
      return;
    }
    
    // Calculate score based on correct answers
    const correctAnswers = answersToUse.filter(
      (answer, index) => answer === questionsToUse[index]?.correctanswer
    ).length;
    
    const totalQuestions = questionsToUse.length;
    const score = Math.round((correctAnswers / totalQuestions) * 100);
    
    console.log("Saving performance with score:", score);
    console.log("Subject:", stateWithFallbacks.subject);
    console.log("Category:", stateWithFallbacks.category);
    
    const success = await saveScore(score);
    if (success) {
      setPerformanceSaved(true);
      toast.success("Performance saved to analytics");
    }
  };
  
  // IMPORTANT CHANGE: Always prioritize snapshots for the complete quiz history
  // Only fall back to questions array if there are no snapshots
  // Don't limit the number of questions to 10
  const questionsToUse = allQuestions.length > 0 ? allQuestions : questions;
  const answersToUse = allQuestions.length > 0 ? allAnswers : answers;
  
  console.log("Using questions array from:", allQuestions.length > 0 ? "snapshots" : "props");
  console.log("Total questions to display:", questionsToUse.length);
  console.log("Total answers to display:", answersToUse.length);

  // If no questions received, show placeholder message
  if (!questionsToUse || questionsToUse.length === 0) {
    console.warn("No questions received in Results component");
    return (
      <div className="text-center p-8 bg-white rounded-lg shadow-md">
        <p className="text-gray-600">No quiz questions available to show results.</p>
        <button 
          onClick={handleReset}
          className="mt-4 bg-primary text-white px-4 py-2 rounded hover:bg-primary/80"
        >
          Back to Subjects
        </button>
      </div>
    );
  }

  // Create a deep copy of questions to avoid reference issues
  const validQuestions = Array.isArray(questionsToUse) ? 
    JSON.parse(JSON.stringify(questionsToUse)) : [];
  
  console.log("Questions before any validation in Results:", validQuestions.length);
  console.log("Question IDs before validation:", validQuestions.map(q => q.id));
  
  // Create a new array for answers - filter out unattempted ones
  let validAnswers = [...answersToUse];
  
  console.log("Before validation - Questions:", validQuestions.length, "Answers:", validAnswers.length);
    
  // Ensure the answers array is at least as long as the questions array
  while (validAnswers.length < validQuestions.length) {
    validAnswers.push(null);
  }

  // Trim excess answers if needed
  if (validAnswers.length > validQuestions.length) {
    validAnswers = validAnswers.slice(0, validQuestions.length);
  }

  console.log("After validation in Results:", {
    validQuestionsCount: validQuestions.length,
    validAnswersCount: validAnswers.length,
    originalQuestionsCount: questionsToUse.length,
    originalAnswersCount: answersToUse.length,
    validQuestionsIds: validQuestions.map(q => q.id)
  });

  // Process questions to ensure all have proper metadata
  const processedQuestions = processQuestionsWithDifficulty(validQuestions);
  
  // Ensure every question has the correct metadata and a unique ID
  const questionsWithCorrectMetadata = processedQuestions.map((q, index) => {
    // Ensure question has an ID
    const questionId = q.id || `temp-${index}-${Math.random().toString(36).substring(2, 15)}`;
    
    // Validate question_type
    let safeQuestionType: "mcq" | "true_false" | "fill_blank" = "mcq";
    if (q.question_type === "mcq" || q.question_type === "true_false" || q.question_type === "fill_blank") {
      safeQuestionType = q.question_type;
    }

    // Normalize difficulty
    const normalizedDifficulty = normalizeDifficulty(q.difficulty || difficulty || 'medium');
    
    // Ensure essential metadata is set
    return {
      ...q,
      id: questionId,
      subject: q.subject || subject || stateWithFallbacks.subject || 'General',
      category: q.category || category || stateWithFallbacks.category || 'General',
      difficulty: normalizedDifficulty,
      question_type: safeQuestionType
    } as Question;
  });

  console.log("Passing to EnhancedResults:", {
    questionCount: questionsWithCorrectMetadata.length,
    validAnswersCount: validAnswers.length,
    questionsIds: questionsWithCorrectMetadata.map(q => q.id),
    subjects: questionsWithCorrectMetadata.map(q => q.subject),
    categories: questionsWithCorrectMetadata.map(q => q.category),
    sessionId: sessionId
  });

  return (
    <div>
      {/* Add Save to Analytics Button */}
      <div className="mb-4 flex justify-end">
        <Button
          onClick={handleSavePerformance}
          className={`${
            performanceSaved ? 'bg-green-600' : 'bg-blue-600'
          } text-white hover:bg-opacity-90 transition-colors`}
          disabled={isSaving || performanceSaved}
        >
          <Save className="mr-2 h-4 w-4" />
          {isSaving ? 'Saving...' : performanceSaved ? 'Saved to Analytics' : 'Save to Analytics'}
        </Button>
      </div>
      
      <EnhancedResults 
        questions={questionsWithCorrectMetadata} 
        answers={validAnswers} 
        onReset={handleReset}
        subject={stateWithFallbacks.subject}
        category={stateWithFallbacks.category}
        difficulty={difficulty}
        sessionId={sessionId}
      />
    </div>
  );
};

export default React.memo(Results);
